import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { articleContents } from '@/mocks/articleContents';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://joyagoocoupon.net';

const ArticlePage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const article = articles.find((a) => a.slug === slug);
  const content = articleContents[slug || ''];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | JoyaGoo Coupon`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', article.excerpt);

      // Canonical
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `${SITE_URL}/${slug}`;

      // Schema.org Article JSON-LD
      const schemaId = 'article-jsonld';
      let existing = document.getElementById(schemaId);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Organization',
          name: 'JoyaGoo Coupon',
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'JoyaGoo Coupon',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/ecd9c2a833b45c83e224d75a48614e57.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/${slug}`,
        },
        url: `${SITE_URL}/${slug}`,
      };
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
    window.scrollTo(0, 0);

    return () => {
      const s = document.getElementById('article-jsonld');
      if (s) s.remove();
    };
  }, [slug, article]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/" className="text-orange-500 hover:underline cursor-pointer">← Back to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-orange-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-700">{article.categoryLabel}</span>
            </div>
            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 ${
              article.category === 'core' ? 'bg-orange-100 text-orange-700' :
              article.category === 'support' ? 'bg-green-100 text-green-700' :
              'bg-sky-100 text-sky-700'
            }`}>
              {article.categoryLabel}
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">{article.title}</h1>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
            </div>
          </div>
        </div>

        {/* Cover Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-6 mb-10">
          <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          </div>
        </div>

        {/* Content + Sidebar */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-16">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Article Body */}
            <article className="flex-1 min-w-0">
              <div
                className="prose prose-gray max-w-none prose-headings:font-extrabold prose-headings:text-gray-900 prose-a:text-orange-500 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-li:text-gray-700 prose-p:text-gray-700 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }}
              />

              {/* CTA Box */}
              <div className="mt-10 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Save with JoyaGoo?</h3>
                <p className="text-gray-600 text-sm mb-5">Use the latest <strong>joyagoo coupon code</strong> and save up to 50% on your next order.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href="https://repsw2c.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-shopping-bag-3-line"></i>
                    joyagoo coupon
                  </a>
                  <a href="https://repsw2c.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                    Buy It Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-20 space-y-6">
                {/* Quick Links */}
                <div className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
                  <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">Top Coupon Pages</h3>
                  <ul className="space-y-2">
                    {[
                      { label: 'Joyagoo Coupon Code', to: '/joyagoo-coupon-code' },
                      { label: 'Joyagoo Promo Code', to: '/joyagoo-promo-code' },
                      { label: 'Joyagoo Review', to: '/joyagoo-review' },
                      { label: 'Joyagoo Shipping', to: '/joyagoo-shipping' },
                      { label: 'How to Use Joyagoo', to: '/how-to-use-joyagoo' },
                      { label: 'Joyagoo Spreadsheet', to: '/joyagoo-spreadsheet' },
                      { label: 'Joyagoo Sheets', to: '/joyagoo-sheets' },
                    ].map((l) => (
                      <li key={l.to}>
                        <Link to={l.to} className="flex items-center gap-2 text-sm text-gray-700 hover:text-orange-500 transition-colors cursor-pointer">
                          <i className="ri-arrow-right-s-line text-orange-400"></i>
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Store CTA */}
                <div className="bg-gray-900 rounded-2xl p-5 text-center">
                  <i className="ri-store-2-line text-orange-400 text-3xl mb-3 block"></i>
                  <h3 className="font-bold text-white mb-2">Visit Main Store</h3>
                  <p className="text-gray-400 text-xs mb-4">Shop all categories with your joyagoo coupon</p>
                  <a href="https://repsw2c.com/" target="_blank" rel="noopener noreferrer" className="block w-full py-2.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer text-sm whitespace-nowrap">
                    Buy It Now →
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* Related Articles */}
          <div className="mt-14">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((rel) => (
                <Link key={rel.id} to={`/${rel.slug}`} className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  <div className="w-full h-36 overflow-hidden">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm line-clamp-2 group-hover:text-orange-500 transition-colors">{rel.title}</h3>
                    <p className="text-xs text-gray-400 mt-2">{rel.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
